import { useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Check, ChevronsUpDown, Loader2 } from "lucide-react";
import {
  authClient,
  organization,
  useActiveOrganization,
  useListOrganizations,
} from "@/lib/auth";
import { cn } from "@/lib/utils";

/**
 * Workspace switcher.
 *
 * The same email routinely belongs to several workspaces, and a person's role
 * differs between them — admin here, plain user there. Switching therefore
 * changes what the API returns *and* what the UI may offer, so the whole query
 * cache is cleared on switch rather than refetched: showing one workspace's
 * recordings under another's name, even for a frame, is the kind of bug people
 * don't forgive in a multi-tenant product.
 */
export function WorkspaceSwitcher() {
  const { t } = useTranslation("shell");
  const queryClient = useQueryClient();
  const { data: organizations } = useListOrganizations();
  const { data: active } = useActiveOrganization();
  const [open, setOpen] = useState(false);
  const [switchingTo, setSwitchingTo] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  // With one workspace there is nothing to switch between; a dropdown that only
  // ever contains the current item is noise.
  if (!organizations || organizations.length <= 1) {
    return active ? (
      <span className="text-[13px] font-medium text-text-muted">{active.name}</span>
    ) : null;
  }

  async function select(organizationId: string) {
    if (organizationId === active?.id) {
      setOpen(false);
      return;
    }
    setSwitchingTo(organizationId);
    await organization.setActive({ organizationId });
    // Drop everything: every cached response was scoped to the old workspace.
    queryClient.clear();
    await authClient.getSession({ query: { disableCookieCache: true } });
    setSwitchingTo(null);
    setOpen(false);
  }

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        aria-haspopup="listbox"
        aria-expanded={open}
        className={cn(
          "inline-flex items-center gap-2 rounded-lg px-2.5 py-1.5",
          "text-[13px] font-medium text-text transition-colors hover:bg-surface-hover",
        )}
      >
        <span className="max-w-[180px] truncate">{active?.name ?? t("workspace.select")}</span>
        <ChevronsUpDown size={13} className="shrink-0 text-text-faint" />
      </button>

      {open ? (
        <div
          role="listbox"
          className={cn(
            "absolute left-0 z-50 mt-1.5 min-w-[240px] overflow-hidden rounded-xl",
            "border border-border bg-surface p-1 shadow-[var(--shadow-lg)]",
          )}
        >
          {organizations.map((org: { id: string; name: string }) => {
            const isActive = org.id === active?.id;
            const isSwitching = switchingTo === org.id;
            return (
              <button
                key={org.id}
                role="option"
                aria-selected={isActive}
                type="button"
                disabled={switchingTo !== null}
                onClick={() => void select(org.id)}
                className={cn(
                  "flex w-full items-center justify-between gap-3 rounded-lg px-2.5 py-2",
                  "text-left text-[13px] transition-colors",
                  "hover:bg-surface-hover disabled:opacity-60",
                  isActive ? "text-text" : "text-text-muted",
                )}
              >
                <span className="truncate">{org.name}</span>
                {isSwitching ? (
                  <Loader2 size={14} className="shrink-0 animate-spin" />
                ) : isActive ? (
                  <Check size={14} className="shrink-0 text-accent" />
                ) : null}
              </button>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}
